import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useLayoutContext } from "../context/LayoutContext";
import { API_URLS } from "../App";
import api from "../hooks/api";
import {
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import ModalScreen from "../components/common/ModalScreen";

const CompoundManageMismatchedPrices: React.FC<{}> = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { error, setError, success, setSuccess, setLoading } =
    useLayoutContext();
  const [data, setData] = React.useState<ComponentWithResellerPriceMismatch[]>(
    [],
  );
  const [updatingComponent, setUpdatingComponent] = React.useState<
    string | null
  >(null);
  const [expandedComponents, setExpandedComponents] = useState<Set<number>>(
    new Set(),
  );

  const toggleComponent = (key: number) => {
    const newExpanded = new Set(expandedComponents);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedComponents(newExpanded);
  };

  const fetchMismatchedPrices = async () => {
    setLoading(true);
    api
      .get(API_URLS.COMPOUNDS + "/_view/mismatch_price_reseller")
      .then(
        (response) => {
          setData(response.data || []);
        },
        (error) => {
          setError(error.message || "Failed to fetch mismatched prices");
          console.warn("Error fetching mismatched prices:", error.message);
        },
      )
      .catch((err: any) => {
        setError(err.message || "Failed to fetch mismatched prices");
        console.warn("Error fetching mismatched prices:", err.message);
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    if (isAuthenticated()) {
      fetchMismatchedPrices();
    } else {
      navigate("/login", { state: { from: pathname } });
    }
  }, [isAuthenticated, pathname, navigate]);

  const [successResult, setSuccessResult] = useState<{
    count: number;
    ids: string[];
  } | null>(() => {
    const saved = sessionStorage.getItem("mismatchedPrices_successResult");
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (successResult) {
      sessionStorage.setItem(
        "mismatchedPrices_successResult",
        JSON.stringify(successResult),
      );
    } else {
      sessionStorage.removeItem("mismatchedPrices_successResult");
    }
  }, [successResult]);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    productReference: string;
    componentTypeKey: string;
    compoundIds: string[];
  } | null>(null);

  const handleFixPriceClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    productReference: string,
    componentTypeKey: string,
    compoundIds: string[],
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setConfirmModal({
      isOpen: true,
      productReference,
      componentTypeKey,
      compoundIds,
    });
  };

  const executeFixPrice = async () => {
    if (!confirmModal) return;

    const { productReference, componentTypeKey, compoundIds } = confirmModal;
    const componentKey = `${productReference}-${componentTypeKey}-${compoundIds.join(",")}`;

    setConfirmModal(null); // Close modal
    setUpdatingComponent(componentKey);
    setError(null);
    setSuccess(null);
    setSuccessResult(null);

    try {
      const response = await api.post(
        API_URLS.COMPOUNDS + "/_actions/fix_component_price",
        null,
        {
          params: {
            productReference,
            componentTypeKey,
            compoundIds: compoundIds.join(","),
          },
        },
      );

      if (response.data.success) {
        setSuccessResult({
          count: response.data.updatedCompounds,
          ids: response.data.updatedCompoundIds || [],
        });
        setSuccess(
          `Preços atualizados com sucesso em ${response.data.updatedCompounds} computador(es)!`,
        );
        // Refresh the data to remove the fixed component from the list
        await fetchMismatchedPrices();
      } else {
        setError(response.data.message || "Falha ao atualizar preços");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Falha ao atualizar preços do componente",
      );
      console.error("Error fixing component price:", err);
    } finally {
      setUpdatingComponent(null);
    }
  };

  if (!data) return null;
  if (!isAuthenticated()) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Acesso Negado
          </h1>
          <p className="text-gray-600">
            Você deve estar logado para visualizar esta página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex flex-col gap-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <span className="text-3xl font-bold text-gray-900 tracking-tight">
              Componentes com Preços Diferentes do ERP
            </span>
            <p className="text-gray-500 mt-1">
              Lista de componentes cujos preços configurados diferem dos preços
              atuais no sistema ERP (diferença superior a 1€)
            </p>
          </div>
        </div>

        {/* Components Grid */}
        {data.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhum Componente com Diferenças de Preço
            </h3>
            <p className="text-gray-600">
              Todos os componentes estão sincronizados com os preços do ERP.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {data.map((item, key) => (
              <div
                key={key}
                className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                {/* Component Header */}
                <div
                  className="border-b border-gray-200 px-6 py-4 bg-gray-50 rounded-t-lg flex justify-between items-center cursor-pointer"
                  onClick={() => toggleComponent(key)}
                >
                  <div className="flex items-center">
                    {expandedComponents.has(key) ? (
                      <ChevronUp className="h-5 w-5 text-gray-600 mr-3" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-600 mr-3" />
                    )}
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {item.component_name}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">Referência:</span>{" "}
                        {item.product_reference} •{" "}
                        <span className="font-medium">Produto:</span>{" "}
                        {item.product_name}
                      </p>
                    </div>
                  </div>
                  {item.price_mismatch_with_compound.length >= 1 && (
                    <button
                      onClick={(e) => {
                        if (!expandedComponents.has(key)) {
                          return;
                        }
                        e.stopPropagation();
                        const allCompoundIds =
                          item.price_mismatch_with_compound.flatMap((m) =>
                            m.compounds.map((c) => c.compound_id),
                          );
                        handleFixPriceClick(
                          e,
                          item.product_reference,
                          item.component_type_key,
                          allCompoundIds,
                        );
                      }}
                      className={`flex items-center px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium shadow-sm 
                        ${item.price_mismatch_with_compound.length > 1 ? "bg-blue-600 hover:bg-blue-700 cursor-pointer" : "bg-blue-400 hover:bg-blue-700"} 
                        ${expandedComponents.has(key) ? "" : "cursor-not-allowed opacity-50"}
                        `}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Corrigir Todos (
                      {item.price_mismatch_with_compound.reduce(
                        (acc, curr) => acc + curr.compounds.length,
                        0,
                      )}
                      )
                    </button>
                  )}
                </div>

                {/* Price Mismatches */}
                {expandedComponents.has(key) && (
                  <div className="p-6 space-y-6">
                    {item.price_mismatch_with_compound.map((mismatch, idx) => {
                      const componentKey = `${item.product_reference} -${item.component_type_key} `;
                      const isUpdating = updatingComponent === componentKey;

                      return (
                        <div
                          key={idx}
                          className="border border-gray-200 rounded-lg p-5 bg-gray-50"
                        >
                          {/* Price Information Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                            {/* REV4 Prices */}
                            <div className="space-y-3">
                              <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                                Preços REV4
                              </h4>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">
                                    Configurado:
                                  </span>
                                  <span className="text-sm font-medium text-gray-900">
                                    {mismatch.defined_rev_price.toFixed(2)} €
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">
                                    ERP Atual:
                                  </span>
                                  <span className="text-sm font-medium text-gray-900">
                                    {mismatch.erp_rev_price.toFixed(2)} €
                                  </span>
                                </div>
                                {mismatch.price_override && (
                                  <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                                    <span className="text-sm text-gray-600">
                                      Preço Redefinido:
                                    </span>
                                    <span className="text-sm font-medium text-purple-600">
                                      {mismatch.price_override.toFixed(2)} €
                                    </span>
                                  </div>
                                )}
                                <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                                  <span className="text-sm font-semibold text-gray-700">
                                    Diferença:
                                  </span>
                                  <span
                                    className={`text - sm font - bold ${
                                      mismatch.difference_rev_price > 0
                                        ? "text-yellow-600"
                                        : "text-red-600"
                                    } `}
                                  >
                                    {mismatch.difference_rev_price > 0
                                      ? "+"
                                      : ""}
                                    {mismatch.difference_rev_price.toFixed(2)} €
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Cost Prices */}
                            <div className="space-y-3">
                              <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                                Preços de Custo
                              </h4>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">
                                    Configurado:
                                  </span>
                                  <span className="text-sm font-medium text-gray-900">
                                    {mismatch.defined_cost_price.toFixed(2)} €
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">
                                    ERP Atual:
                                  </span>
                                  <span className="text-sm font-medium text-gray-900">
                                    {mismatch.erp_cost_price.toFixed(2)} €
                                  </span>
                                </div>
                                <div className="flex justify-between items-center pt-4 border-t border-gray-300">
                                  <span className="text-sm font-semibold text-gray-700">
                                    Diferença:
                                  </span>
                                  <span
                                    className={`text - sm font - bold ${
                                      mismatch.difference_cost_price > 0
                                        ? "text-yellow-600"
                                        : "text-red-600"
                                    } `}
                                  >
                                    {mismatch.difference_cost_price > 0
                                      ? "+"
                                      : ""}
                                    {mismatch.difference_cost_price.toFixed(2)}{" "}
                                    €
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action Button */}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                            <button
                              onClick={(e) =>
                                handleFixPriceClick(
                                  e,
                                  item.product_reference,
                                  item.component_type_key,
                                  mismatch.compounds.map((c) => c.compound_id),
                                )
                              }
                              disabled={isUpdating}
                              className={`flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                                isUpdating
                                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                  : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md active:scale-95"
                              } `}
                            >
                              {isUpdating ? (
                                <>
                                  <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                                  A Atualizar...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="h-5 w-5 mr-2" />
                                  Corrigir Preço ({
                                    mismatch.compounds.length
                                  }{" "}
                                  {mismatch.compounds.length === 1
                                    ? "computador"
                                    : "computadores"}
                                  )
                                </>
                              )}
                            </button>

                            {/* Compounds List */}
                            <details className="group">
                              <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 list-none">
                                Ver Computadores ({mismatch.compounds.length})
                              </summary>
                              <div className="mt-3 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md p-3">
                                <ul className="space-y-1">
                                  {mismatch.compounds.map((compound, index) => (
                                    <li
                                      key={index}
                                      onClick={() =>
                                        navigate(
                                          "/catalog/compound/" +
                                            encodeURIComponent(
                                              compound.compound_id,
                                            ),
                                        )
                                      }
                                      className="text-xs font-mono text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded cursor-pointer transition-colors"
                                    >
                                      {compound.compound_id} •{" "}
                                      {compound.optional ? "Opcional" : "Base"}{" "}
                                      • {compound.status}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </details>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModal && (
          <ModalScreen
            title="Confirmar Atualização"
            state={confirmModal.isOpen ? "confirm" : "closed"}
            onClose={() => setConfirmModal(null)}
            footer={
              <div className="flex justify-end space-x-3 p-4">
                <button
                  onClick={() => setConfirmModal(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={executeFixPrice}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Confirmar
                </button>
              </div>
            }
          >
            <div className="p-4">
              <p className="text-gray-700 mb-4">
                Tem a certeza que pretende atualizar os preços deste componente
                em{" "}
                <span className="font-bold">
                  {confirmModal.compoundIds.length}
                </span>{" "}
                computador(es)?
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <p className="font-medium text-yellow-800 mb-2">
                  Esta ação irá:
                </p>
                <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                  <li>Atualizar o preço configurado para o preço ERP atual</li>
                  <li>Remover qualquer preço redefinido</li>
                  <li>
                    Aplicar as alterações apenas aos computadores selecionados
                  </li>
                </ul>
              </div>
            </div>
          </ModalScreen>
        )}

        {/* Loading Overlay */}
        {updatingComponent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
              <RefreshCw className="h-10 w-10 text-blue-600 animate-spin mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">
                A atualizar preços...
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Por favor aguarde enquanto processamos a atualização.
              </p>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {successResult && (
          <ModalScreen
            title="Atualização Concluída"
            state="info"
            onClose={() => setSuccessResult(null)}
            footer={
              <div className="flex justify-end p-4">
                <button
                  onClick={() => setSuccessResult(null)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Fechar
                </button>
              </div>
            }
          >
            <div className="p-4">
              <div className="flex items-center mb-4 text-green-600">
                <CheckCircle2 className="h-6 w-6 mr-2" />
                <span className="font-semibold text-lg">
                  {successResult.count} computador(es) atualizado(s) com
                  sucesso!
                </span>
              </div>
              <p className="text-gray-600 mb-4">
                Os seguintes computadores foram atualizados e recalculados:
              </p>
              <div className="bg-gray-50 rounded-lg border border-gray-200 max-h-60 overflow-y-auto">
                <ul className="divide-y divide-gray-200">
                  {successResult.ids.map((id) => (
                    <li
                      key={id}
                      className="p-3 hover:bg-gray-100 transition-colors flex justify-between items-center"
                    >
                      <span className="font-mono text-sm font-medium text-gray-700">
                        {id}
                      </span>
                      <a
                        href={`/catalog/compound/${encodeURIComponent(id)}`}
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                      >
                        Ver / Editar
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ModalScreen>
        )}
      </div>
    </div>
  );
};

export default CompoundManageMismatchedPrices;
